import { observer } from 'mobx-lite';
import { LinearProgress, CircularProgress, Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { MainStoreContextItem } from '../../Stores/MainStore';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { FaceDetectionStatus, type FaceDetectionStatusType } from '../../Stores/FaceServiceStore';
import { Close, Check, Warning, ErrorOutline } from '@mui/icons-material';
import { accentColors, glassChipSx } from '../glassStyles';

// --- Constants ---

const DEBOUNCE_MS = 800;

const STATUS_MESSAGES: Record<FaceDetectionStatus, string | null> = {
    [FaceDetectionStatus.ProcessingFace]: 'Processing Face...',
    [FaceDetectionStatus.FaceTooSmall]: 'Move Closer',
    [FaceDetectionStatus.FaceQualityTooLow]: 'Face Quality Too Low',
    [FaceDetectionStatus.None]: null,
};

const STATUS_CONFIG: Record<NonNullable<FaceDetectionStatusType>, { icon: ReactNode; color: string }> = {
    processing: { icon: <CircularProgress size={14} sx={{ color: accentColors.info }} />, color: accentColors.info },
    warning: { icon: <Warning sx={{ fontSize: 18, color: accentColors.warning }} />, color: accentColors.warning },
    error: { icon: <ErrorOutline sx={{ fontSize: 18, color: accentColors.error }} />, color: accentColors.error },
};

const motionAnimationProps = {
    initial: { opacity: 0, y: -8, scale: 2 * 0.95 },
    animate: { opacity: 1, y: 0, scale: 2, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, y: -8, scale: 2 * 0.95, transition: { delay: 0.3, duration: 0.3 } },
};

// --- Hooks ---

/** Debounce a value — applies delay when transitioning to `delayedValue`, instant otherwise. */
function useDebounced<T>(value: T, delayWhen: (v: T) => boolean, ms = DEBOUNCE_MS): T {
    const [debounced, setDebounced] = useState(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setDebounced(value), delayWhen(value) ? ms : 0);
        return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- delayWhen is a stable predicate from call site
    }, [value, ms]);

    return debounced;
}

// --- Helpers ---

function getQualityInfo(pct: number) {
    if (pct >= 100) return { label: 'Perfect!', icon: <Check sx={{ fontSize: 18 }} />, accent: accentColors.success };
    if (pct < 10) return { label: 'Difficult to See You', icon: <Close sx={{ fontSize: 18 }} />, accent: accentColors.error };
    return { label: null, icon: null, accent: pct >= 75 ? accentColors.warning : accentColors.error };
}

// --- Components ---

// eslint-disable-next-line react-refresh/only-export-components
const NotificationWrapper: React.FC<{ statusKey: string; children: ReactNode }> = ({ statusKey, children }) => (
    <motion.div key={statusKey} {...motionAnimationProps} style={{ transformOrigin: 'top center' }}>
        {children}
    </motion.div>
);

export const QualityProgressOverlay = observer(() => {
    const store = MainStoreContextItem.useStore();
    const { faceIdealQuality, minFaceQuality } = store.settingsStore;
    const { status, hasActiveStatus, statusType } = store.faceServiceStore;

    const { isEventOverlayActive } = store.faceServiceStore;
    const lastTrackedFace = store.faceServiceStore.highestQualityFaces.get(store.faceServiceStore.lastTrackedFaceId ?? '');
    const qualityPct = Math.min(((lastTrackedFace?.quality ?? 0) / faceIdealQuality) * 100, 100);
    const shouldShow = !isEventOverlayActive && (Boolean(lastTrackedFace) || hasActiveStatus);

    // Debounce to prevent rapid flickering (delay hide/set, instant show/clear)
    const debouncedShow = useDebounced(shouldShow, (v) => !v);
    const debouncedStatusType = useDebounced(statusType, (v) => v !== null);
    const debouncedMessage = useDebounced(STATUS_MESSAGES[status], (v) => v !== null);

    const qualityInfo = getQualityInfo(qualityPct);

    return (
        <AnimatePresence mode="wait">
            {debouncedShow && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.1 } }}
                    exit={{ opacity: 0, transition: { delay: 0.5, duration: 1 } }}
                    style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 3 }}
                >
                    {debouncedStatusType && STATUS_CONFIG[debouncedStatusType] ? (
                        <NotificationWrapper statusKey={debouncedStatusType}>
                            <Box sx={glassChipSx}>
                                {STATUS_CONFIG[debouncedStatusType].icon}
                                <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                        {debouncedMessage}
                                    </Typography>
                                    {debouncedStatusType === 'error' && (
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                                            Min required: {minFaceQuality.toFixed(2)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </NotificationWrapper>
                    ) : (
                        <NotificationWrapper statusKey="progress">
                            <Box sx={glassChipSx}>
                                {qualityInfo.icon && (
                                    <Box sx={{ color: qualityInfo.accent, display: 'flex' }}>
                                        {qualityInfo.icon}
                                    </Box>
                                )}
                                {qualityInfo.label ? (
                                    <Typography variant="body2" fontWeight={500}>
                                        {qualityInfo.label}
                                    </Typography>
                                ) : (
                                    <Box sx={{ width: 80 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={qualityPct}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                bgcolor: 'rgba(255,255,255,0.15)',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 2,
                                                    background: `linear-gradient(90deg, ${accentColors.error} 0%, ${accentColors.warning} 50%, ${accentColors.success} 100%)`,
                                                    backgroundSize: `${(100 / Math.max(qualityPct, 1)) * 100}% 100%`,
                                                },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </NotificationWrapper>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
});

QualityProgressOverlay.displayName = 'QualityProgressOverlay';
